import { useQuery } from '@tanstack/react-query';
import type {
  RequestDashboardDto,
  BdsUserRef,
  Paginated,
  RequestCountsDto,
  RequestSummaryDto,
  RequestView,
  RequestViewDto,
  SampleDto,
  TaskListItemDto,
  TaskViewDto,
  WorkflowEventDto,
} from '@mexp/shared';
import { http } from '@/api/client';

export const drKeys = {
  all: ['dr'] as const,
  list: (view: RequestView, q: string, page: number, filters: RequestListFilters = {}) => ['dr', 'list', view, q, page, filters] as const,
  counts: ['dr', 'counts'] as const,
  request: (id: string) => ['dr', 'request', id] as const,
  timeline: (id: string) => ['dr', 'timeline', id] as const,
  myTasks: (status: string) => ['dr', 'my-tasks', status] as const,
  task: (id: string) => ['dr', 'task', id] as const,
  taskTimeline: (id: string) => ['dr', 'task-timeline', id] as const,
  bdsReviewers: ['dr', 'bds-reviewers'] as const,
  queue: (params: Record<string, unknown>) => ['dr', 'queue', params] as const,
  sample: (id: string) => ['dr', 'sample', id] as const,
};

export function useRequestCounts(enabled = true) {
  return useQuery({ queryKey: drKeys.counts, queryFn: () => http.get<RequestCountsDto>('/dossier-requests/counts'), enabled });
}

/** The staff dashboard's numbers (coordinators). */
export function useRequestDashboard(enabled = true) {
  return useQuery({ queryKey: ['dr', 'dashboard'], queryFn: () => http.get<RequestDashboardDto>('/dossier-requests/dashboard'), enabled });
}

/** Narrows the Document requests list: the client stage, the country, the requisition type (client requisitions). */
export interface RequestListFilters {
  stage?: string;
  country?: string;
  requestType?: string;
  /** A worklist (`REQUEST_FLAGS`). */
  flag?: string;
}

export function useRequestList(view: RequestView, q: string, page: number, limit = 25, filters: RequestListFilters = {}) {
  return useQuery({
    queryKey: drKeys.list(view, q, page, filters),
    queryFn: () => http.get<Paginated<RequestSummaryDto>>('/dossier-requests', { view, q, page, limit, ...filters }),
    placeholderData: (prev) => prev,
  });
}

export function useRequest(id: string) {
  return useQuery({ queryKey: drKeys.request(id), queryFn: () => http.get<RequestViewDto>(`/dossier-requests/${id}`) });
}

/** The sample asked for with the request and its artwork (BD updates them in BDS), or null when none was. */
export function useRequestSample(id: string) {
  return useQuery({
    queryKey: drKeys.sample(id),
    queryFn: () => http.get<{ sample: SampleDto | null }>(`/dossier-requests/${id}/sample`),
    select: (d) => d.sample,
    enabled: !!id,
  });
}

export function useRequestTimeline(id: string, enabled = true) {
  return useQuery({
    queryKey: drKeys.timeline(id),
    queryFn: () => http.get<{ items: WorkflowEventDto[] }>(`/dossier-requests/${id}/timeline`),
    select: (d) => d.items,
    enabled,
  });
}

export function useMyTasks(status: 'open' | 'done') {
  return useQuery({
    queryKey: drKeys.myTasks(status),
    queryFn: () => http.get<{ items: TaskListItemDto[] }>('/work-items/mine', { status }),
    select: (d) => d.items,
  });
}

export function useTask(id: string) {
  return useQuery({ queryKey: drKeys.task(id), queryFn: () => http.get<TaskViewDto>(`/work-items/${id}`) });
}

export function useTaskTimeline(id: string) {
  return useQuery({
    queryKey: drKeys.taskTimeline(id),
    queryFn: () => http.get<{ items: WorkflowEventDto[] }>(`/work-items/${id}/timeline`),
    select: (d) => d.items,
    enabled: !!id,
  });
}

/** SC-NBD users in BDS who can review a dossier (loaded live from BDS). */
export function useBdsReviewers(enabled = true) {
  return useQuery({
    queryKey: drKeys.bdsReviewers,
    queryFn: () => http.get<{ items: BdsUserRef[] }>('/dossier-requests/bds-reviewers'),
    select: (d) => d.items,
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}
